import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PublicIcon from '@mui/icons-material/Public';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import Loading from '../../components/shared/Loading';
import MediaUpload from '../../components/shared/DirectUpload';
import { useQuery } from '@apollo/client/react';
import { GET_TRAINING_PLAN_SESSIONS } from '../../graphql/training_plan_session';

const levelLabel = (level) => {
  if (`${level}` === '2') return 'Advanced';
  if (`${level}` === '1') return 'Intermediate';
  return 'Beginner';
};

const statusLabel = (status) => {
  if (`${status}` === '0') return 'Draft';
  if (`${status}` === '2') return 'Private';
  return 'Published';
};

const weekRangeLabel = (weekNumber) => {
  const start = new Date(2026, 5, 12 + (weekNumber - 1) * 7);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const startLabel = start.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const endLabel = end.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return `${startLabel} - ${endLabel}`;
};

const workoutMeta = (event) => {
  const title = event?.title ?? '';
  if (/recovery|mobility/i.test(title)) return ['20 min', 'Day 3'];
  if (/conditioning|cardio/i.test(title))
    return ['30 min', `Day ${event.dayOfWeek}`];
  return ['45 min', `Day ${event.dayOfWeek}`];
};

const percentLabel = (value) => {
  if (value == null) {
    return '-';
  }

  return `${value}%`;
};

const getAverageCompletion = (trainingPlan, sessions) => {
  if (trainingPlan.enrolled > 0 && trainingPlan.completed != null) {
    return Math.min(
      100,
      Math.round((trainingPlan.completed / trainingPlan.enrolled) * 100)
    );
  }

  if (!sessions.length) {
    return null;
  }

  const completedSessions = sessions.filter(
    (session) => session.status === 'COMPLETED' || session.completed
  ).length;
  return Math.min(100, Math.round((completedSessions / sessions.length) * 100));
};

const getAverageAdherence = (trainingPlan, sessions, workoutCount) => {
  const totalEventCount = Math.max(workoutCount, trainingPlan.eventCount || 0);

  if (!sessions.length || !totalEventCount) {
    return null;
  }

  const totalSessionAdherence = sessions.reduce((sum, session) => {
    const completedEventCount =
      session.completedEventCount ?? session.completedEvents?.length ?? 0;
    const sessionTotalEventCount = session.totalEventCount || totalEventCount;

    if (!sessionTotalEventCount) {
      return sum;
    }

    return sum + completedEventCount / sessionTotalEventCount;
  }, 0);

  return Math.min(
    100,
    Math.round((totalSessionAdherence / sessions.length) * 100)
  );
};

const SettingRow = ({ icon, title, subtitle, onEdit }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={{ py: 2 }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: '50%',
          backgroundColor: '#eef2f7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#40536b',
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
        <Typography sx={{ color: '#40536b', fontSize: 13 }}>
          {subtitle}
        </Typography>
      </Box>
    </Stack>
    <Button variant="outlined" size="small" onClick={onEdit}>
      Toggle
    </Button>
  </Stack>
);

export default function TrainingPlanDetailsEditor(props) {
  const [trainingPlan, setTrainingPlan] = useState(props.trainingPlan);
  const [showUploader, setShowUploader] = useState(false);
  const { data: sessionsData } = useQuery(GET_TRAINING_PLAN_SESSIONS, {
    variables: {
      input: {
        trainingPlanId: props.trainingPlan?._id,
        limit: 100,
      },
    },
    skip: !props.trainingPlan?._id,
  });

  useEffect(() => {
    setTrainingPlan(props.trainingPlan);
  }, [props.trainingPlan]);

  const weeks = useMemo(() => {
    const events = trainingPlan?.events ?? [];
    const grouped = events.reduce((acc, event) => {
      const weekNumber = Math.max(1, Math.ceil((event.day ?? 1) / 7));
      const dayOfWeek = (((event.day ?? 1) - 1) % 7) + 1;
      acc[weekNumber] = acc[weekNumber] ?? [];
      acc[weekNumber].push({ ...event, dayOfWeek });
      return acc;
    }, {});

    return Object.keys(grouped)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .map((weekNumber) => ({
        weekNumber: parseInt(weekNumber),
        events: grouped[weekNumber].sort((a, b) => (a.day ?? 0) - (b.day ?? 0)),
      }));
  }, [trainingPlan]);

  if (!trainingPlan) {
    return <Loading />;
  }

  const saveChanges = (changes) => {
    setTrainingPlan(changes);
    props?.onSave(changes);
  };

  const handleEdit = (event) => {
    const changes = {
      ...trainingPlan,
      [event.target.name]: event.target.value,
    };
    setTrainingPlan(changes);
  };

  const handleBlurSave = () => {
    props?.onSave(trainingPlan);
  };

  const handleAddTag = () => {
    const tag = prompt('Add tag', '');
    if (!tag?.trim()) return;
    saveChanges({
      ...trainingPlan,
      tags: [...(trainingPlan.tags ?? []), tag.trim()],
    });
  };

  const handleDeleteTag = (tag) => {
    saveChanges({
      ...trainingPlan,
      tags: (trainingPlan.tags ?? []).filter((item) => item !== tag),
    });
  };

  const handleUploadSuccess = (uploadedRes) => {
    if (!uploadedRes.image) return;
    saveChanges({ ...trainingPlan, coverImages: [uploadedRes.image] });
    setShowUploader(false);
  };

  const handleUploadRemove = () => {
    saveChanges({ ...trainingPlan, coverImages: [] });
    setShowUploader(false);
  };

  const coverImageUrl = trainingPlan.coverImages?.[0]?.url;
  const visibleWeeks = weeks.slice(0, 3);
  const weekCount = Math.max(1, weeks.length);
  const workoutCount = trainingPlan.events?.length ?? 0;
  const trainingPlanSessions = sessionsData?.trainingPlanSessions || [];
  const averageCompletion = getAverageCompletion(
    trainingPlan,
    trainingPlanSessions
  );
  const averageAdherence = getAverageAdherence(
    trainingPlan,
    trainingPlanSessions,
    workoutCount
  );

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Stack spacing={0.5}>
            <Card
              variant="outlined"
              sx={{ borderColor: '#dbe3ee', borderRadius: 1 }}
            >
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>
                      Title
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      name="title"
                      value={trainingPlan.title ?? ''}
                      onChange={handleEdit}
                      onBlur={handleBlurSave}
                      inputProps={{ maxLength: 60 }}
                    />
                    <Typography
                      sx={{ color: '#40536b', fontSize: 13, mt: 0.75 }}
                    >
                      Max 60 characters
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>
                      Tags
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {(trainingPlan.tags ?? []).map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          onDelete={() => handleDeleteTag(tag)}
                          sx={{ backgroundColor: '#eef2f7' }}
                        />
                      ))}
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddTag}
                        sx={{ height: 34 }}
                      >
                        Add
                      </Button>
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>
                      Description
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      name="desc"
                      value={trainingPlan.desc ?? ''}
                      onChange={handleEdit}
                      onBlur={handleBlurSave}
                      inputProps={{ maxLength: 540 }}
                    />
                    <Typography
                      sx={{ color: '#40536b', fontSize: 13, mt: 0.75 }}
                    >
                      Max 540 characters
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card
              variant="outlined"
              sx={{ borderColor: '#dbe3ee', borderRadius: 1 }}
            >
              <CardContent sx={{ pb: 1 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
                      Program Structure
                    </Typography>
                    <Typography sx={{ color: '#40536b', fontSize: 14 }}>
                      {workoutCount} workouts • {weekCount} weeks
                    </Typography>
                  </Box>
                  <Button variant="outlined" onClick={props.onEditWorkouts}>
                    Edit Workouts
                  </Button>
                </Stack>

                {visibleWeeks.map((week) => (
                  <Box key={week.weekNumber}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{ py: 1.5, backgroundColor: '#fbfcfe' }}
                    >
                      <Typography sx={{ fontWeight: 800, minWidth: 70 }}>
                        Week {week.weekNumber}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${week.events.length} workouts`}
                        sx={{
                          backgroundColor: '#eef2f7',
                          color: '#40536b',
                          fontWeight: 700,
                        }}
                      />
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarMonthIcon
                          sx={{ fontSize: 18, color: '#40536b' }}
                        />
                        <Typography sx={{ color: '#40536b', fontSize: 14 }}>
                          {weekRangeLabel(week.weekNumber)}
                        </Typography>
                      </Stack>
                      <Box sx={{ flex: 1 }} />
                      <IconButton size="small">
                        <MoreHorizIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    {week.events.slice(0, 2).map((event) => {
                      const [duration, dayLabel] = workoutMeta(event);
                      return (
                        <Stack
                          key={event._id}
                          direction="row"
                          alignItems="center"
                          spacing={1.5}
                          sx={{ py: 1.25, borderTop: '1px solid #edf1f6' }}
                        >
                          <DragIndicatorIcon
                            sx={{ color: '#8090a3', fontSize: 18 }}
                          />
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              backgroundColor: '#6254e8',
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <FitnessCenterIcon sx={{ fontSize: 15 }} />
                          </Box>
                          <Typography sx={{ fontWeight: 700, minWidth: 190 }}>
                            {event.title}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="center"
                            sx={{ minWidth: 90 }}
                          >
                            <ScheduleIcon
                              sx={{ fontSize: 16, color: '#40536b' }}
                            />
                            <Typography sx={{ color: '#40536b', fontSize: 14 }}>
                              {duration}
                            </Typography>
                          </Stack>
                          <Chip
                            size="small"
                            label={dayLabel}
                            sx={{
                              backgroundColor: '#dcf7e7',
                              color: '#118847',
                              fontWeight: 700,
                            }}
                          />
                        </Stack>
                      );
                    })}
                    <Divider />
                  </Box>
                ))}

                {workoutCount > 0 && (
                  <Button
                    sx={{ mt: 1, mx: 'auto', display: 'flex' }}
                    onClick={props.onEditWorkouts}
                  >
                    View all {workoutCount} workouts
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card
              variant="outlined"
              sx={{
                width: 210,
                borderColor: '#dbe3ee',
                borderRadius: 1,
                mt: 2,
              }}
            >
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <CheckCircleIcon sx={{ color: '#2dbd70' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                      All changes saved
                    </Typography>
                    <Typography sx={{ color: '#40536b', fontSize: 12 }}>
                      Last saved just now
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack spacing={1}>
            <Card
              variant="outlined"
              sx={{ borderColor: '#dbe3ee', borderRadius: 1 }}
            >
              <CardContent>
                <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 1.5 }}>
                  Cover Image
                </Typography>
                {coverImageUrl && (
                  <Box
                    sx={{
                      position: 'relative',
                      borderRadius: 1,
                      overflow: 'hidden',
                      aspectRatio: '16 / 9',
                      backgroundColor: '#101820',
                    }}
                  >
                    <Box
                      component="img"
                      src={coverImageUrl}
                      alt="Training plan cover"
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <Button
                      variant="contained"
                      color="inherit"
                      startIcon={<CameraAltOutlinedIcon />}
                      onClick={() => setShowUploader(!showUploader)}
                      sx={{ position: 'absolute', top: 12, right: 12 }}
                    >
                      Change Image
                    </Button>
                    <Button
                      variant="contained"
                      color="inherit"
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => setShowUploader(!showUploader)}
                      sx={{ position: 'absolute', right: 12, bottom: 12 }}
                    >
                      Edit
                    </Button>
                  </Box>
                )}
                {(!coverImageUrl || showUploader) && (
                  <Box sx={{ mt: coverImageUrl ? 2 : 0 }}>
                    <MediaUpload
                      images={trainingPlan.coverImages}
                      onError={() => {}}
                      onSuccess={handleUploadSuccess}
                      onRemove={handleUploadRemove}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card
              variant="outlined"
              sx={{ borderColor: '#dbe3ee', borderRadius: 1 }}
            >
              <CardContent>
                <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 2 }}>
                  Plan Stats
                </Typography>
                <Grid container>
                  {[
                    [workoutCount, 'Workouts'],
                    [weekCount, 'Weeks'],
                    [trainingPlan.enrolled ?? 0, 'Athletes Enrolled'],
                    [percentLabel(averageCompletion), 'Avg. Completion'],
                    [percentLabel(averageAdherence), 'Avg. Adherence'],
                    ['-', 'Athlete Rating'],
                  ].map(([value, label], index) => (
                    <Grid
                      item
                      xs={4}
                      key={label}
                      sx={{
                        p: 1.5,
                        borderRight: index % 3 === 2 ? 0 : '1px solid #edf1f6',
                        borderBottom: index < 3 ? '1px solid #edf1f6' : 0,
                      }}
                    >
                      <Typography sx={{ fontWeight: 800, fontSize: 22 }}>
                        {value}
                      </Typography>
                      <Typography sx={{ color: '#40536b', fontSize: 13 }}>
                        {label}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            <Card
              variant="outlined"
              sx={{ borderColor: '#dbe3ee', borderRadius: 1 }}
            >
              <CardContent>
                <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 1 }}>
                  Program Settings
                </Typography>
                <SettingRow
                  icon={<CalendarMonthIcon fontSize="small" />}
                  title={
                    trainingPlan.hasSchedule
                      ? 'Scheduled Program'
                      : 'Workout Collection'
                  }
                  subtitle={
                    trainingPlan.hasSchedule
                      ? 'Athletes follow the workouts on specific days.'
                      : 'Athletes can browse this plan as a collection.'
                  }
                  onEdit={() =>
                    saveChanges({
                      ...trainingPlan,
                      hasSchedule: !trainingPlan.hasSchedule,
                    })
                  }
                />
                <Divider />
                <SettingRow
                  icon={<SignalCellularAltIcon fontSize="small" />}
                  title={levelLabel(trainingPlan.level)}
                  subtitle="Level"
                  onEdit={() =>
                    saveChanges({
                      ...trainingPlan,
                      level: `${(parseInt(trainingPlan.level ?? 0) + 1) % 3}`,
                    })
                  }
                />
                <Divider />
                <SettingRow
                  icon={
                    <PublicIcon fontSize="small" sx={{ color: '#12a45a' }} />
                  }
                  title={statusLabel(trainingPlan.status)}
                  subtitle="Visible to athletes and can be assigned."
                  onEdit={() =>
                    saveChanges({
                      ...trainingPlan,
                      status: `${(parseInt(trainingPlan.status ?? 1) + 1) % 3}`,
                    })
                  }
                />
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
